import { useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import {
  useOrgStorage,
  useStorageOrgs,
  type OrgStorageRow,
  type StorageSnapshot,
} from '../../api/admin-stripe'

type SortKey = 'name' | 'sqlite' | 'recording' | 'blob' | 'total' | 'date'

/**
 * Cross-org sortable storage table with per-row 30-day sparkline drill-down.
 *
 * Inline <svg> sparkline — no chart library dependency. The drill-down
 * lazy-loads ``GET /orgs/{id}/storage`` only when a row is expanded.
 */
export function OrgStorageTable() {
  const orgs = useStorageOrgs()
  const [sortKey, setSortKey] = useState<SortKey>('total')
  const [sortDesc, setSortDesc] = useState(true)
  const [expanded, setExpanded] = useState<string | null>(null)

  function toggleSort(k: SortKey) {
    if (k === sortKey) setSortDesc(!sortDesc)
    else { setSortKey(k); setSortDesc(true) }
  }

  if (orgs.isLoading) {
    return (
      <section className="rounded-lg border border-border bg-surface p-6 text-center text-sm text-text-secondary">
        Loading storage…
      </section>
    )
  }
  if (!orgs.data || orgs.data.orgs.length === 0) {
    return (
      <section className="rounded-lg border border-border bg-surface p-6 text-center text-sm text-text-secondary">
        No snapshots yet — click &ldquo;Snapshot now&rdquo; above.
      </section>
    )
  }

  const rows = [...orgs.data.orgs].sort((a, b) => {
    const dir = sortDesc ? -1 : 1
    switch (sortKey) {
      case 'name':
        return dir * String(a.org_name ?? '').localeCompare(String(b.org_name ?? ''))
      case 'sqlite': return dir * (a.sqlite_gb - b.sqlite_gb)
      case 'recording': return dir * (a.recording_gb - b.recording_gb)
      case 'blob': return dir * (a.blob_gb - b.blob_gb)
      case 'total': return dir * (a.total_gb - b.total_gb)
      case 'date': return dir * String(a.measured_date ?? '').localeCompare(String(b.measured_date ?? ''))
    }
  })

  return (
    <section className="overflow-hidden rounded-lg border border-border bg-surface">
      <header className="border-b border-border px-4 py-3 font-medium">
        Storage by organization
      </header>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-surface-elevated text-xs uppercase text-text-secondary">
            <tr>
              <th className="w-6 px-2 py-2"></th>
              <Th label="Org" sortKey="name" current={sortKey} desc={sortDesc} onClick={toggleSort} />
              <Th label="SQLite GB" sortKey="sqlite" current={sortKey} desc={sortDesc} onClick={toggleSort} align="right" />
              <Th label="Recording GB" sortKey="recording" current={sortKey} desc={sortDesc} onClick={toggleSort} align="right" />
              <Th label="Blob GB" sortKey="blob" current={sortKey} desc={sortDesc} onClick={toggleSort} align="right" />
              <Th label="Total GB" sortKey="total" current={sortKey} desc={sortDesc} onClick={toggleSort} align="right" />
              <Th label="Measured" sortKey="date" current={sortKey} desc={sortDesc} onClick={toggleSort} />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((r) => (
              <Row
                key={r.org_id}
                row={r}
                expanded={expanded === r.org_id}
                onToggle={() =>
                  setExpanded((cur) => (cur === r.org_id ? null : r.org_id))
                }
              />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function Th({
  label, sortKey, current, desc, onClick, align,
}: {
  label: string
  sortKey: SortKey
  current: SortKey
  desc: boolean
  onClick: (k: SortKey) => void
  align?: 'right'
}) {
  const arrow = current === sortKey ? (desc ? '↓' : '↑') : ''
  return (
    <th
      className={`cursor-pointer px-2 py-2 ${align === 'right' ? 'text-right' : 'text-left'}`}
      onClick={() => onClick(sortKey)}
    >
      {label} {arrow}
    </th>
  )
}

function Row({
  row, expanded, onToggle,
}: {
  row: OrgStorageRow
  expanded: boolean
  onToggle: () => void
}) {
  const detail = useOrgStorage(expanded ? row.org_id : undefined)
  return (
    <>
      <tr className="hover:bg-surface-elevated/40">
        <td className="px-2 py-2">
          <button
            onClick={onToggle}
            className="text-text-secondary hover:text-primary"
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
        </td>
        <td className="px-2 py-2">
          <div className="font-medium">{row.org_name ?? row.org_id}</div>
          <div className="text-xs text-text-secondary">{row.org_slug ?? row.org_id}</div>
        </td>
        <td className="px-2 py-2 text-right font-mono">{row.sqlite_gb.toFixed(4)}</td>
        <td className="px-2 py-2 text-right font-mono">{row.recording_gb.toFixed(4)}</td>
        <td className="px-2 py-2 text-right font-mono">{row.blob_gb.toFixed(4)}</td>
        <td className="px-2 py-2 text-right font-mono font-semibold">{row.total_gb.toFixed(4)}</td>
        <td className="px-2 py-2 font-mono text-xs">{row.measured_date ?? '—'}</td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={7} className="bg-background px-4 py-3">
            {detail.isLoading ? (
              <div className="text-xs text-text-secondary">Loading 30-day history…</div>
            ) : !detail.data ? (
              <div className="text-xs text-error">Failed to load detail.</div>
            ) : (
              <StorageHistorySparkline history={detail.data.history} />
            )}
          </td>
        </tr>
      )}
    </>
  )
}

function StorageHistorySparkline({
  history,
}: {
  history: StorageSnapshot[]
}) {
  if (history.length === 0) {
    return <div className="text-xs text-text-secondary">No history yet.</div>
  }
  // History returns newest-first; reverse for plotting.
  const data = [...history].reverse()
  const totals = data.map((s) => s.total_bytes)
  const max = Math.max(...totals, 1)
  const min = Math.min(...totals)
  const w = 320
  const h = 48
  const stepX = data.length > 1 ? w / (data.length - 1) : 0
  const points = data
    .map((s, i) => {
      const x = i * stepX
      const norm = (s.total_bytes - min) / (max - min || 1)
      const y = h - norm * h
      return `${x.toFixed(1)},${y.toFixed(1)}`
    })
    .join(' ')

  return (
    <div className="space-y-2">
      <div className="text-xs text-text-secondary">
        Last {data.length} day(s) — peak{' '}
        <span className="font-mono">{(max / 1e9).toFixed(3)} GB</span>
      </div>
      <svg width={w} height={h} className="text-primary">
        <polyline
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          points={points}
        />
      </svg>
      <table className="w-full max-w-md text-xs">
        <thead className="text-text-secondary">
          <tr>
            <th className="text-left">Date</th>
            <th className="text-right">SQLite</th>
            <th className="text-right">Rec</th>
            <th className="text-right">Total</th>
            <th className="text-right">Reported?</th>
          </tr>
        </thead>
        <tbody>
          {history.map((s) => (
            <tr key={s.id}>
              <td className="font-mono">{s.measured_date}</td>
              <td className="text-right font-mono">
                {(s.sqlite_bytes / 1e9).toFixed(3)}
              </td>
              <td className="text-right font-mono">
                {(s.recording_bytes / 1e9).toFixed(3)}
              </td>
              <td className="text-right font-mono font-semibold">
                {(s.total_bytes / 1e9).toFixed(3)}
              </td>
              <td className="text-right font-mono">
                {s.reported_to_stripe_at ? '✓' : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
